export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  service: string;
  message: string;
}

export interface EmailResult {
  success: boolean;
  message: string;
  messageId?: string;
}

/**
 * Sends contact form data to the Lambda API which handles Mailgun integration
 */
export const sendContactForm = async (formData: ContactFormData): Promise<EmailResult> => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://q1p938pt08.execute-api.us-east-1.amazonaws.com/dev';
    const apiUrl = `${backendUrl}/api/contact`;
    
    console.log('Sending contact form to:', apiUrl);
    console.log('Form data:', formData);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    const result = await response.json();
    console.log('Response body:', result);

    if (!response.ok) {
      throw new Error(result.message || `HTTP error! status: ${response.status}`);
    }

    return result;
  } catch (error) {
    console.error('Error sending contact form:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to send contact form'
    };
  }
};

/**
 * Tests the Lambda API connection
 */
export const testBackendConnection = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://q1p938pt08.execute-api.us-east-1.amazonaws.com/dev';
    
    // Lambda doesn't have a health endpoint, so we'll test with a simple OPTIONS request
    const response = await fetch(`${backendUrl}/api/contact`, {
      method: 'OPTIONS',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || `HTTP error! status: ${response.status}`);
    }

    return {
      success: true,
      message: 'Lambda API connection successful',
      details: result
    };
  } catch (error) {
    console.error('Backend connection test failed:', error);
    
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to connect to backend'
    };
  }
};

/**
 * Validates contact form data before sending
 */
export const validateContactForm = (formData: ContactFormData): {
  isValid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!formData.firstName.trim()) {
    errors.push('First name is required');
  }

  if (!formData.lastName.trim()) {
    errors.push('Last name is required');
  }

  if (!formData.email.trim()) {
    errors.push('Email is required');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.push('Please enter a valid email address');
    }
  }

  if (!formData.company.trim()) {
    errors.push('Company is required');
  }

  if (!formData.message.trim()) {
    errors.push('Message is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
